import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ShoppingCart, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n/LanguageContext';
import {
  PurchaseIntent,
  authLink,
  clearPurchaseIntent,
  readPurchaseIntent,
  samePage,
} from '@/lib/purchaseIntent';

/**
 * SAFETY NET for anyone who drifted away mid-purchase — opened the email
 * link in a new tab, clicked a menu item, came back tomorrow. On every page
 * (except the purchase page itself and the auth pages, which have their own
 * guidance) it shows ONE line: what they were buying + ONE button to continue.
 *
 * Signed in  -> Continue goes straight back to the lead / plan checkout.
 * Signed out -> Continue goes to sign-up with the intent still attached.
 */
const ResumePurchaseBanner: React.FC = () => {
  const { profile, loading } = useAuth();
  const { path, lang } = useI18n();
  const location = useLocation();
  const es = lang === 'es';
  const [intent, setIntent] = useState<PurchaseIntent | null>(() => readPurchaseIntent());

  useEffect(() => {
    const sync = () => setIntent(readPurchaseIntent());
    sync();
    window.addEventListener('gg-intent', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('gg-intent', sync);
      window.removeEventListener('storage', sync);
    };
  }, [location.pathname]);

  if (loading || !intent) return null;
  const here = location.pathname;
  // Never on the purchase page itself, the auth pages, the post-payment page
  // or the verification page (each has its own, more specific instructions).
  if (samePage(intent.returnTo, here)) return null;
  if (
    ['/login', '/register', '/payment-complete', '/verify-email', '/admin', '/reset-password'].some((p) =>
      here.startsWith(path(p)),
    )
  )
    return null;

  const href = profile
    ? intent.returnTo
    : authLink(path('/register'), intent.returnTo, intent.kind === 'lead' ? 'lead' : 'plan');

  const what =
    intent.kind === 'lead'
      ? es
        ? 'Estaba comprando un lead'
        : 'You were buying a lead'
      : es
      ? 'Estaba uniéndose a una membresía'
      : 'You were joining a membership';

  return (
    <div className="border-b border-orange-200 bg-orange-50">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <ShoppingCart className="h-4 w-4 shrink-0 text-orange-600" />
        <p className="min-w-0 flex-1 text-sm font-semibold text-orange-900">
          {what}
          {intent.label ? <span className="font-bold"> — {intent.label}</span> : null}
        </p>
        <Link
          to={href}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600"
        >
          {es ? 'Continuar' : 'Continue'} <ArrowRight className="h-4 w-4" />
        </Link>
        <button
          type="button"
          onClick={clearPurchaseIntent}
          aria-label={es ? 'Descartar' : 'Dismiss'}
          className="rounded-lg p-2 text-orange-700 hover:bg-orange-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ResumePurchaseBanner;
